// The pictures of a match for the socials, drawn on a canvas (1080x1350, the portrait post of Instagram
// and Facebook): the result, and the MVP. Images of the storage come through the API (assets?path=), which
// sends the CORS headers the storage does not: a canvas with an image without them cannot be saved

const API_URL = process.env.VUE_APP_API_HOST_DOMAIN
const STORAGE_URL = 'https://storage.googleapis.com/'
export const WIDTH = 1080
export const HEIGHT = 1350

// a blob as a data: url (a blob: url is refused by some embedded browsers)
export function toDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error)
        reader.readAsDataURL(blob)
    })
}

// a url of the storage (through the API) or a bundled asset; null when it cannot be loaded
export function loadImage(source) {
    if (!source) return Promise.resolve(null)
    const path = source.startsWith(STORAGE_URL) ? decodeURIComponent(source.slice(STORAGE_URL.length)) : null
    const load = src => new Promise(resolve => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = () => resolve(null)
        image.src = src
    })
    if (!path) return load(source)
    return fetch(`${API_URL}/assets?path=${encodeURIComponent(path)}`)
        .then(resp => (resp.ok ? resp.blob() : null))
        .then(blob => (blob ? toDataUrl(blob).then(load) : null))
        .catch(() => null)
}

// an image of the storage as a data: url, through the API: an <img> with it goes into a saved picture too; null
// when it cannot be loaded
export function storageDataUrl(path) {
    const source = storageUrl(path)
    if (!source || !source.startsWith(STORAGE_URL)) return Promise.resolve(null)
    return fetch(`${API_URL}/assets?path=${encodeURIComponent(decodeURIComponent(source.slice(STORAGE_URL.length)))}`)
        .then(resp => (resp.ok ? resp.blob() : null))
        .then(blob => (blob ? toDataUrl(blob) : null))
        .catch(() => null)
}

function canvas() {
    const element = document.createElement('canvas')
    element.width = WIDTH
    element.height = HEIGHT
    return element
}

function toBlob(element) {
    return new Promise((resolve, reject) => {
        element.toBlob(blob => (blob ? resolve(blob) : reject(new Error('The picture could not be made'))), 'image/png')
    })
}

// the fonts of the theme, before the first text is drawn
async function loadFonts(theme) {
    if (!document.fonts || !document.fonts.load) return
    await Promise.all([theme.titleFont, theme.textFont, theme.numberFont]
        .map(font => document.fonts.load(`700 40px ${font}`, 'ΑΒΓ 0123').catch(() => null)))
}

function font(weight, size, family) {
    return `${weight} ${size}px ${family}, Arial, sans-serif`
}

// the text in one line of maxWidth: the size goes down to minSize, then the text is cut
function fitText(ctx, text, x, y, maxWidth, { size, minSize = size * 0.55, weight = 700, family, align = 'center', color }) {
    let current = size
    ctx.font = font(weight, current, family)
    while (current > minSize && ctx.measureText(text).width > maxWidth) {
        current -= 2
        ctx.font = font(weight, current, family)
    }
    let value = String(text)
    while (value.length > 1 && ctx.measureText(value).width > maxWidth) {
        value = value.slice(0, -2) + '…'
    }
    ctx.textAlign = align
    ctx.fillStyle = color
    ctx.fillText(value, x, y)
}

function roundRect(ctx, x, y, width, height, radius) {
    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.arcTo(x + width, y, x + width, y + height, radius)
    ctx.arcTo(x + width, y + height, x, y + height, radius)
    ctx.arcTo(x, y + height, x, y, radius)
    ctx.arcTo(x, y, x + width, y, radius)
    ctx.closePath()
}

function gradient(ctx, x0, y0, x1, y1, colors) {
    const fill = ctx.createLinearGradient(x0, y0, x1, y1)
    colors.forEach((color, index) => fill.addColorStop(index / Math.max(1, colors.length - 1), color))
    return fill
}

// the board: the colour of the site, a glow of its accent and its stripes
function background(ctx, theme) {
    ctx.fillStyle = theme.background
    ctx.fillRect(0, 0, WIDTH, HEIGHT)
    const glow = ctx.createRadialGradient(WIDTH / 2, 0, 0, WIDTH / 2, 0, HEIGHT * 0.8)
    glow.addColorStop(0, theme.glow)
    glow.addColorStop(1, 'rgba(0, 0, 0, 0)')
    ctx.fillStyle = glow
    ctx.fillRect(0, 0, WIDTH, HEIGHT)
    ctx.save()
    ctx.globalAlpha = 0.9
    ctx.fillStyle = gradient(ctx, 0, 0, WIDTH, 0, theme.gradient)
    ctx.fillRect(0, 0, WIDTH, 14)
    ctx.fillRect(0, HEIGHT - 14, WIDTH, 14)
    ctx.globalAlpha = 0.07
    for (let i = -4; i < 12; i++) {
        ctx.beginPath()
        ctx.moveTo(i * 160, HEIGHT)
        ctx.lineTo(i * 160 + 70, HEIGHT)
        ctx.lineTo(i * 160 + 70 + HEIGHT * 0.6, 0)
        ctx.lineTo(i * 160 + HEIGHT * 0.6, 0)
        ctx.closePath()
        ctx.fill()
    }
    ctx.restore()
}

// an image inside a box, whole (contain) or filling it (cover)
function drawImage(ctx, image, x, y, width, height, cover = false) {
    const scale = cover
        ? Math.max(width / image.width, height / image.height)
        : Math.min(width / image.width, height / image.height)
    const drawWidth = image.width * scale
    const drawHeight = image.height * scale
    ctx.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight)
}

// a team logo on a white disc; without one the colour of the team and its initial
function teamBadge(ctx, logo, team, cx, cy, radius, theme) {
    ctx.save()
    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    ctx.fillStyle = logo ? '#ffffff' : (team.color || theme.accent)
    ctx.fill()
    ctx.lineWidth = Math.max(4, radius * 0.06)
    ctx.strokeStyle = team.color || theme.accent
    ctx.stroke()
    ctx.clip()
    if (logo) {
        const inner = radius * 1.45
        drawImage(ctx, logo, cx - inner / 2, cy - inner / 2, inner, inner)
    } else {
        ctx.fillStyle = '#ffffff'
        ctx.font = font(700, radius, theme.titleFont)
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(String(team.name || '?').trim().charAt(0).toUpperCase(), cx, cy + radius * 0.05)
        ctx.textBaseline = 'alphabetic'
    }
    ctx.restore()
}

function header(ctx, theme, logo, top, height) {
    if (logo) {
        const width = height * logo.width / logo.height
        drawImage(ctx, logo, (WIDTH - width) / 2, top, width, height)
    }
}

function footer(ctx, theme) {
    fitText(ctx, theme.site, WIDTH / 2, HEIGHT - 44, 900, { size: 30, weight: 700, family: theme.textFont, color: theme.muted })
}

// the url of a stored path ("bucket/team/...")
export function storageUrl(path) {
    if (!path) return null
    return /^https?:\/\//i.test(path) ? path : STORAGE_URL + path
}

// match: {title, subtitle, team1: {name, logo (url), color, score}, team2, periods: [{label, team1, team2}]}
export async function renderResultImage(match, theme) {
    await loadFonts(theme)
    const [siteLogo, logo1, logo2] = await Promise.all([loadImage(theme.logo), loadImage(match.team1.logo), loadImage(match.team2.logo)])
    const element = canvas()
    const ctx = element.getContext('2d')
    background(ctx, theme)
    header(ctx, theme, siteLogo, 60, theme.logoHeight)

    let y = 60 + theme.logoHeight + 70
    fitText(ctx, match.title.toUpperCase(), WIDTH / 2, y, 940, { size: 40, weight: 700, family: theme.titleFont, color: theme.accent })
    y += 50
    fitText(ctx, match.subtitle, WIDTH / 2, y, 940, { size: 30, weight: 400, family: theme.textFont, color: theme.muted })

    // the pill of the final score
    y += 40
    ctx.fillStyle = gradient(ctx, WIDTH / 2 - 110, 0, WIDTH / 2 + 110, 0, theme.gradient)
    roundRect(ctx, WIDTH / 2 - 110, y, 220, 56, 28)
    ctx.fill()
    fitText(ctx, 'ΤΕΛΙΚΟ', WIDTH / 2, y + 39, 200, { size: 30, weight: 700, family: theme.titleFont, color: theme.onAccent })

    const score1 = Number(match.team1.score) || 0
    const score2 = Number(match.team2.score) || 0
    const sides = [
        { team: match.team1, logo: logo1, x: 270, score: score1, won: score1 > score2 },
        { team: match.team2, logo: logo2, x: 810, score: score2, won: score2 > score1 }
    ]
    const badgeY = y + 270
    sides.forEach(side => {
        teamBadge(ctx, side.logo, side.team, side.x, badgeY, 150, theme)
        fitText(ctx, side.team.name, side.x, badgeY + 225, 440, { size: 46, weight: 700, family: theme.titleFont, color: theme.text })
        // the score of the team under its name, the winner bright
        ctx.font = font(700, 170, theme.numberFont)
        ctx.textAlign = 'center'
        ctx.fillStyle = side.won ? theme.text : theme.muted
        ctx.fillText(String(side.score), side.x, badgeY + 420)
    })
    // the line between the two scores
    ctx.fillStyle = gradient(ctx, WIDTH / 2 - 30, 0, WIDTH / 2 + 30, 0, theme.gradient)
    ctx.fillRect(WIDTH / 2 - 30, badgeY + 352, 60, 10)

    // the score of every period
    const periods = (match.periods || []).filter(period => period.team1 !== '-' || period.team2 !== '-')
    if (periods.length) {
        const top = badgeY + 480
        const gap = 16
        const width = Math.min(200, (940 - gap * (periods.length - 1)) / periods.length)
        const total = width * periods.length + gap * (periods.length - 1)
        periods.forEach((period, index) => {
            const x = (WIDTH - total) / 2 + index * (width + gap)
            ctx.fillStyle = theme.card
            roundRect(ctx, x, top, width, 110, 18)
            ctx.fill()
            fitText(ctx, period.label, x + width / 2, top + 38, width - 20, { size: 26, weight: 700, family: theme.textFont, color: theme.accent })
            fitText(ctx, `${period.team1}-${period.team2}`, x + width / 2, top + 88, width - 20, { size: 38, weight: 700, family: theme.numberFont, color: theme.text })
        })
    }
    footer(ctx, theme)
    return toBlob(element)
}

// mvp: {name, photo, team: {name, logo, color}, pir, pirLabel ('PIR' when none), line: [{label, value}], result}
export async function renderMvpImage(mvp, theme) {
    await loadFonts(theme)
    const [siteLogo, photo, logo] = await Promise.all([loadImage(theme.logo), loadImage(mvp.photo), loadImage(mvp.team.logo)])
    const element = canvas()
    const ctx = element.getContext('2d')
    background(ctx, theme)
    header(ctx, theme, siteLogo, 50, theme.logoHeight * 0.8)

    // the photo in a frame of the gradient, the team logo on its corner: the frame takes the shape of the photo
    // (the photos of the players are mostly portraits), so the player is not cut
    const size = 560
    const frame = photo ? Math.round(Math.min(size, Math.max(380, size * photo.width / photo.height))) : size
    const x = (WIDTH - frame) / 2
    const y = 50 + theme.logoHeight * 0.8 + 50
    ctx.fillStyle = gradient(ctx, x, y, x + frame, y + size, theme.gradient)
    roundRect(ctx, x - 10, y - 10, frame + 20, size + 20, 44)
    ctx.fill()
    ctx.save()
    roundRect(ctx, x, y, frame, size, 36)
    ctx.fillStyle = theme.card
    ctx.fill()
    ctx.clip()
    if (photo) {
        // what is left over is cut from the bottom, never from the head
        const scale = Math.max(frame / photo.width, size / photo.height)
        ctx.drawImage(photo, x + (frame - photo.width * scale) / 2, y, photo.width * scale, photo.height * scale)
    }
    ctx.restore()
    teamBadge(ctx, logo, mvp.team, x + frame - 10, y + size - 30, 80, theme)

    let top = y + size + 150
    ctx.font = font(700, 76, theme.titleFont)
    ctx.textAlign = 'center'
    ctx.fillStyle = gradient(ctx, WIDTH / 2 - 260, 0, WIDTH / 2 + 260, 0, theme.gradient)
    ctx.fillText('MVP ΑΓΩΝΑ', WIDTH / 2, top)
    top += 76
    fitText(ctx, mvp.name, WIDTH / 2, top, 960, { size: 62, weight: 700, family: theme.titleFont, color: theme.text })
    top += 46
    fitText(ctx, mvp.team.name, WIDTH / 2, top, 900, { size: 32, weight: 400, family: theme.textFont, color: theme.muted })

    // PIR, then the line of the player
    const boxes = [{ label: mvp.pirLabel || 'PIR', value: mvp.pir, main: true }].concat(mvp.line)
    const gap = 14
    const width = (960 - gap * (boxes.length - 1)) / boxes.length
    top += 34
    boxes.forEach((box, index) => {
        const left = 60 + index * (width + gap)
        ctx.fillStyle = box.main ? gradient(ctx, left, top, left + width, top + 120, theme.gradient) : theme.card
        roundRect(ctx, left, top, width, 120, 18)
        ctx.fill()
        fitText(ctx, String(box.value), left + width / 2, top + 64, width - 16, { size: 50, weight: 700, family: theme.numberFont, color: box.main ? theme.onAccent : theme.text })
        fitText(ctx, box.label, left + width / 2, top + 100, width - 16, { size: 22, weight: 700, family: theme.textFont, color: box.main ? theme.onAccent : theme.muted })
    })
    fitText(ctx, mvp.result, WIDTH / 2, HEIGHT - 96, 960, { size: 30, weight: 700, family: theme.textFont, color: theme.text })
    footer(ctx, theme)
    return toBlob(element)
}
